import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Popover, Tooltip } from 'antd';
import { DiffOutlined } from '@ant-design/icons';
import { IconButton } from '../../common/components/icon-button.component';
import { AnnotationSaveEvent } from '../models/annotation-save-event.model';
import { AnnotationDiffResult } from '../models/annotation-diff.model';
import { Granularity } from '../actions/version-history.actions';
import { filterSaveEvents } from '../selectors/version-history.selectors';

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0 8px;
  font-size: 12px;
  gap: 4px;
`;

const DiffFilterButton = styled.button<{ active: boolean }>`
  background: ${p => (p.active ? '#e6f7ff' : 'transparent')};
  border: 1px solid ${p => (p.active ? '#1890ff' : '#d9d9d9')};
  border-radius: 3px;
  cursor: pointer;
  font-size: 11px;
  font-weight: 600;
  min-width: 40px;
  height: 18px;
  text-align: center;
  padding: 0 6px;
  line-height: 18px;
  white-space: nowrap;
  margin-left: 4px;

  &:hover {
    border-color: #1890ff;
  }
`;

const Added = styled.span`
  color: #52c41a;
`;
const Removed = styled.span`
  color: #f5222d;
`;
const Changed = styled.span`
  color: #faad14;
`;

const DiffToggle = styled.span<{ active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0 6px;
  font-size: 18px;
  color: ${p => (p.active ? '#1890ff' : 'inherit')};

  &:hover {
    color: #1890ff;
  }
`;

const VersionButton = styled.button`
  background: transparent;
  border: 1px solid transparent;
  border-radius: 3px;
  cursor: pointer;
  font-size: 12px;
  color: #595959;
  padding: 0 6px;
  white-space: nowrap;
  line-height: 22px;
  height: 22px;

  &:hover {
    border-color: #d9d9d9;
    background: #fafafa;
  }
`;

const MenuContent = styled.div`
  width: 280px;
  display: flex;
  flex-direction: column;
`;

const GranularityToggleWrapper = styled.div`
  display: flex;
  padding: 4px 12px 8px;
`;

const GranularityBtn = styled.button<{ active: boolean }>`
  flex: 1;
  background: ${p => (p.active ? '#1890ff' : '#fafafa')};
  color: ${p => (p.active ? '#fff' : '#595959')};
  border: 1px solid ${p => (p.active ? '#1890ff' : '#d9d9d9')};
  font-size: 11px;
  padding: 3px 0;
  cursor: pointer;
  line-height: 1;

  &:first-child {
    border-radius: 3px 0 0 3px;
  }
  &:last-child {
    border-radius: 0 3px 3px 0;
  }
  &:not(:first-child) {
    margin-left: -1px;
  }
  &:hover {
    color: ${p => (p.active ? '#fff' : '#1890ff')};
    border-color: #1890ff;
    z-index: 1;
    position: relative;
  }
`;

const VersionList = styled.div`
  overflow-y: auto;
  max-height: 280px;
  border-top: 1px solid #f0f0f0;
`;

const VersionItem = styled.button<{ selected: boolean }>`
  display: block;
  width: 100%;
  text-align: left;
  background: ${p => (p.selected ? '#e6f7ff' : 'transparent')};
  border: none;
  padding: 6px 12px;
  font-size: 12px;
  cursor: pointer;
  color: #262626;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    background: ${p => (p.selected ? '#e6f7ff' : '#fafafa')};
  }
`;

function findNearestEventId(
  currentId: number | null,
  allEvents: AnnotationSaveEvent[],
  filteredEvents: AnnotationSaveEvent[],
): number | null {
  if (currentId === null) return null;
  if (filteredEvents.length === 0) return null;
  if (filteredEvents.some(e => e.annotationSaveEventId === currentId))
    return currentId;

  const currentIdx = allEvents.findIndex(
    e => e.annotationSaveEventId === currentId,
  );
  let bestId = filteredEvents[0].annotationSaveEventId;
  let bestDist = Infinity;
  for (const fe of filteredEvents) {
    const idx = allEvents.findIndex(
      e => e.annotationSaveEventId === fe.annotationSaveEventId,
    );
    const dist = Math.abs(idx - currentIdx);
    if (dist < bestDist) {
      bestDist = dist;
      bestId = fe.annotationSaveEventId;
    }
  }
  if (bestId === allEvents[0]?.annotationSaveEventId) return null;
  return bestId;
}

function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function versionTooltip(event: AnnotationSaveEvent | undefined): string {
  if (!event) return '';
  const date = new Date(event.annotationTimestamp);
  const formatted = date.toLocaleString();
  return `${formatted} — ${event.annotatorName}`;
}

type VersionHistoryToolbarProps = {
  saveEvents: AnnotationSaveEvent[];
  filteredEvents: AnnotationSaveEvent[];
  granularity: Granularity;
  selectedSaveEventId: number | null;
  selectedIndex: number;
  loading: boolean;
  diff: AnnotationDiffResult;
  diffModeEnabled: boolean;
  diffFilterActive: boolean;
  onSelectVersion: (saveEventId: number | null) => void;
  onSetGranularity: (g: Granularity) => void;
  onToggleDiffMode: (enabled: boolean) => void;
  onToggleDiffFilter: () => void;
};

export const VersionHistoryToolbar = ({
  saveEvents,
  filteredEvents,
  granularity,
  selectedSaveEventId,
  selectedIndex,
  loading,
  diff,
  diffModeEnabled,
  diffFilterActive,
  onSelectVersion,
  onSetGranularity,
  onToggleDiffMode,
  onToggleDiffFilter,
}: VersionHistoryToolbarProps) => {
  const [menuOpen, setMenuOpen] = useState(false);

  const total = saveEvents.length;
  const isLatest = selectedSaveEventId === null;
  const versionNumber = isLatest ? total : total - selectedIndex;

  const filteredIndex = useMemo(() => {
    if (isLatest) return 0;
    return filteredEvents.findIndex(
      e => e.annotationSaveEventId === selectedSaveEventId,
    );
  }, [filteredEvents, selectedSaveEventId, isLatest]);

  const canGoNewer = !isLatest && filteredIndex > 0;
  const canGoOlder = isLatest
    ? filteredEvents.length > 1
    : filteredIndex < filteredEvents.length - 1;
  const isEarliest = !isLatest && filteredIndex === filteredEvents.length - 1;

  const handleGranularityChange = (g: Granularity) => {
    const filtered = filterSaveEvents(saveEvents, g);
    const nearestId = findNearestEventId(
      selectedSaveEventId,
      saveEvents,
      filtered,
    );
    if (nearestId !== selectedSaveEventId) {
      onSelectVersion(nearestId);
    }
    onSetGranularity(g);
  };

  const handleNewer = () => {
    if (!canGoNewer) return;
    const newerEvent = filteredEvents[filteredIndex - 1];
    if (
      newerEvent.annotationSaveEventId === saveEvents[0]?.annotationSaveEventId
    ) {
      onSelectVersion(null);
    } else {
      onSelectVersion(newerEvent.annotationSaveEventId);
    }
  };

  const handleOlder = () => {
    if (!canGoOlder) return;
    if (isLatest) {
      onSelectVersion(filteredEvents[1].annotationSaveEventId);
    } else {
      onSelectVersion(filteredEvents[filteredIndex + 1].annotationSaveEventId);
    }
  };

  const handleJumpToLatest = () => onSelectVersion(null);

  const handleJumpToEarliest = () =>
    onSelectVersion(
      filteredEvents[filteredEvents.length - 1].annotationSaveEventId,
    );

  const handlePickVersion = (event: AnnotationSaveEvent) => {
    const id = event.annotationSaveEventId;
    if (id === saveEvents[0]?.annotationSaveEventId) {
      onSelectVersion(null);
    } else {
      onSelectVersion(id);
    }
    setMenuOpen(false);
  };

  const { summary } = diff;
  const hasDiff =
    summary.added > 0 || summary.removed > 0 || summary.changed > 0;

  const currentEvent = isLatest ? saveEvents[0] : saveEvents[selectedIndex];

  const menuContent = (
    <MenuContent>
      <GranularityToggleWrapper>
        <GranularityBtn
          active={granularity === 'all'}
          onClick={() => handleGranularityChange('all')}
        >
          All
        </GranularityBtn>
        <GranularityBtn
          active={granularity === 'hourly'}
          onClick={() => handleGranularityChange('hourly')}
        >
          Hourly
        </GranularityBtn>
        <GranularityBtn
          active={granularity === 'daily'}
          onClick={() => handleGranularityChange('daily')}
        >
          Daily
        </GranularityBtn>
      </GranularityToggleWrapper>
      <VersionList>
        {filteredEvents.map(event => {
          const idx = saveEvents.indexOf(event);
          const vNum = total - idx;
          const isSelected =
            (idx === 0 && isLatest) ||
            event.annotationSaveEventId === selectedSaveEventId;
          return (
            <VersionItem
              key={event.annotationSaveEventId}
              selected={isSelected}
              onClick={() => handlePickVersion(event)}
            >
              v{vNum} - {formatTimestamp(event.annotationTimestamp)} -{' '}
              {event.annotatorName}
            </VersionItem>
          );
        })}
      </VersionList>
    </MenuContent>
  );

  return (
    <Wrapper>
      <Tooltip title="Toggle diff view">
        <DiffToggle
          active={diffModeEnabled}
          onClick={() => onToggleDiffMode(!diffModeEnabled)}
        >
          <DiffOutlined />
        </DiffToggle>
      </Tooltip>
      {diffModeEnabled && (
        <>
          <Tooltip title="Jump to earliest">
            <span>
              <IconButton
                iconType="double-left"
                disabled={isEarliest || loading}
                onClick={handleJumpToEarliest}
              />
            </span>
          </Tooltip>
          <Tooltip title="Older version">
            <span>
              <IconButton
                iconType="left"
                disabled={!canGoOlder || loading}
                onClick={handleOlder}
              />
            </span>
          </Tooltip>
          <Tooltip title="Newer version">
            <span>
              <IconButton
                iconType="right"
                disabled={!canGoNewer || loading}
                onClick={handleNewer}
              />
            </span>
          </Tooltip>
          <Tooltip title="Jump to latest">
            <span>
              <IconButton
                iconType="double-right"
                disabled={isLatest || loading}
                onClick={handleJumpToLatest}
              />
            </span>
          </Tooltip>
          <Popover
            content={menuContent}
            trigger="click"
            visible={menuOpen}
            onVisibleChange={setMenuOpen}
            placement="bottomLeft"
          >
            <Tooltip title={versionTooltip(currentEvent)}>
              <VersionButton>
                {isLatest
                  ? `Latest (v${total})`
                  : `v${versionNumber} of ${total}`}
              </VersionButton>
            </Tooltip>
          </Popover>
        </>
      )}
      {diffModeEnabled && hasDiff && (
        <Tooltip title="Toggle diff filter">
          <DiffFilterButton
            active={diffFilterActive}
            onClick={onToggleDiffFilter}
          >
            {summary.added > 0 && <Added>+{summary.added} </Added>}
            {summary.removed > 0 && <Removed>-{summary.removed} </Removed>}
            {summary.changed > 0 && <Changed>~{summary.changed}</Changed>}
          </DiffFilterButton>
        </Tooltip>
      )}
    </Wrapper>
  );
};
